/**
 * Typed quote + ride-request endpoint group for the Vello real clients (S100; M12). Wraps the S091 `vello-api`
 * ride surface — `POST /v1/quotes`, `GET /v1/quotes/:id`, `POST /v1/ride-requests`. The server renders money
 * as decimal-minor-unit strings on the wire (`jsonSafe`), so this group **decodes** the quote's money back to
 * `@vello/core` `Money` (via `parseMoney`) and returns a clean `@vello/fare-quote` `FareQuote` — the rest of
 * the app never touches the wire encoding. Ride creation is idempotent per key: a duplicate returns the same
 * ride (`deduped: true`); a same-key/different-payload replay is a `CONFLICT`.
 *
 * D357 adds the rider live-ride surface (`/v1/rider/rides/*`: active ride, tracking snapshot, rating, receipt) and
 * the consumer market reads (`/v1/rider/markets/*`: served markets, point resolution, ride options).
 */
import { parseMoney, serializeMoney } from '@vello/core';
import { transportError } from './errors.js';
/** The file of a receipt download answer (shared by the rider's and the operator's receipt PDFs). */
export function receiptPdfFileOf(answer) {
    const named = /filename="([^"]+)"/.exec(answer.headers['content-disposition'] ?? '')?.[1];
    return { bytes: answer.bytes, fileName: named ?? 'recibo.pdf', contentType: answer.headers['content-type'] ?? 'application/pdf' };
}
/** Decode a wire quote (string money) into a real `FareQuote` (bigint `Money`). */
export function decodeQuote(wire) {
    const { lines, grossTotal, total, commission, driverNet, coupon, incentive, ...rest } = wire;
    return {
        ...rest,
        ...(coupon !== undefined ? { coupon: { ...coupon, requestedDiscountMinor: BigInt(coupon.requestedDiscountMinor), appliedDiscountMinor: BigInt(coupon.appliedDiscountMinor) } } : {}),
        ...(incentive !== undefined ? { incentive: { ...incentive, requestedDiscountMinor: BigInt(incentive.requestedDiscountMinor), appliedDiscountMinor: BigInt(incentive.appliedDiscountMinor), perRideCapMinor: BigInt(incentive.perRideCapMinor) } } : {}),
        lines: lines.map((l) => ({ code: l.code, amount: parseMoney(l.amount) })),
        grossTotal: parseMoney(grossTotal),
        total: parseMoney(total),
        ...(commission !== undefined ? { commission: parseMoney(commission) } : {}),
        ...(driverNet !== undefined ? { driverNet: parseMoney(driverNet) } : {}),
    };
}
export class QuotesApi {
    client;
    constructor(client) {
        this.client = client;
    }
    async create(input) {
        const res = await this.client.request({ method: 'POST', path: '/v1/quotes', body: input, auth: true });
        return res.ok ? { ok: true, value: decodeQuote(res.value.quote) } : res;
    }
    async get(quoteId) {
        const res = await this.client.request({ method: 'GET', path: `/v1/quotes/${encodeURIComponent(quoteId)}`, auth: true });
        return res.ok ? { ok: true, value: { quote: decodeQuote(res.value.quote), expired: res.value.expired } } : res;
    }
}
export class RidesApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /**
     * Request a ride against a quote. The server answers with the full ride aggregate; this decodes it to a
     * `RideSummary`. (Before D357 the client typed a `ride.id` the server never sent, so the app lost the ride id.)
     */
    async request(input) {
        // Money never crosses the wire as a bigint: the change amount is the decimal minor-unit string with its currency.
        const { changeFor, ...payment } = input.payment;
        const body = { ...input, payment: { ...payment, ...(changeFor !== undefined ? { changeFor: serializeMoney(changeFor) } : {}) } };
        const res = await this.client.request({ method: 'POST', path: '/v1/ride-requests', body, auth: true });
        if (!res.ok)
            return res;
        const ride = res.value.ride?.ride;
        if (ride === undefined || typeof ride.rideId !== 'string' || typeof ride.code !== 'string' || typeof ride.status !== 'string') {
            return { ok: false, error: { code: 'MALFORMED_RESPONSE', status: 0 } };
        }
        return { ok: true, value: { ride: { id: ride.rideId, code: ride.code, status: ride.status }, deduped: res.value.deduped === true } };
    }
    /** Read a ride's durable pre-assignment status (S101) — owner-scoped on the server. */
    status(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/ride-requests/${encodeURIComponent(rideId)}`, auth: true });
    }
    /**
     * Cancel a ride (S101). Before a driver is assigned it is free and needs no reason. After assignment (D418, owner decision
     * D408 #5) it is free too, but only where the market's governed rule allows it (`RiderRideTracking.cancelAfterAssignment`)
     * and with one of its `reasonCodes`; the stable refusals are `RIDER_CANCEL_AFTER_ASSIGNMENT_NOT_ALLOWED` (409),
     * `RIDER_CANCEL_REASON_REQUIRED` / `RIDER_CANCEL_REASON_NOT_ALLOWED` (400) and `RIDE_ALREADY_STARTED` (409). Idempotent.
     */
    cancel(rideId, reason) {
        return this.client.request({ method: 'POST', path: `/v1/ride-requests/${encodeURIComponent(rideId)}/cancel`, body: reason !== undefined ? { reason } : {}, auth: true });
    }
    // ---- D357: the rider live-ride surface -----------------------------------------------------------
    /** The rider's current ride (restored on tab switch / app restart) and a recently completed ride awaiting a rating. */
    async active() {
        const res = await this.client.request({ method: 'GET', path: '/v1/rider/rides/active', auth: true });
        if (!res.ok)
            return res;
        const { active, awaitingRating } = res.value;
        return {
            ok: true,
            value: {
                ...(active !== null && active !== undefined ? { active: decodeRiderRide(active) } : {}),
                ...(awaitingRating !== null && awaitingRating !== undefined ? { awaitingRating: decodeRiderRide(awaitingRating) } : {}),
            },
        };
    }
    /**
     * D445 (R-08): one page of the rider's own ride history, newest first. Pass the previous page's `nextCursor` as `before`
     * for the next (older) page; `nextCursor` is null on the last page. `limit` is 1–20 (default 20).
     */
    async history(page = {}) {
        const qs = new URLSearchParams();
        if (page.limit !== undefined)
            qs.set('limit', String(page.limit));
        if (page.before !== undefined)
            qs.set('before', page.before);
        const query = qs.toString();
        const res = await this.client.request({ method: 'GET', path: `/v1/rider/rides${query !== '' ? `?${query}` : ''}`, auth: true });
        if (!res.ok)
            return res;
        return { ok: true, value: { rides: res.value.rides.map(({ charged, cancellationFee, ...r }) => ({ ...r, ...(charged !== undefined ? { charged: parseMoney(charged) } : {}), ...(cancellationFee !== undefined ? { cancellationFee: parseMoney(cancellationFee) } : {}) })), nextCursor: res.value.nextCursor ?? null } };
    }
    /** The polling snapshot of a ride (status, driver, vehicle + plate, last driver location, ETA). Owner-only. */
    async tracking(rideId) {
        const res = await this.client.request({ method: 'GET', path: `/v1/rider/rides/${encodeURIComponent(rideId)}/tracking`, auth: true });
        if (!res.ok)
            return res;
        const w = res.value;
        return {
            ok: true,
            value: {
                ride: decodeRiderRide(w.ride),
                ...(w.driver !== null && w.driver !== undefined ? { driver: w.driver } : {}),
                ...(w.driverLocation !== null && w.driverLocation !== undefined ? { driverLocation: w.driverLocation } : {}),
                ...(w.eta !== null && w.eta !== undefined ? { eta: w.eta } : {}),
                ...(w.search !== null && w.search !== undefined ? { search: w.search } : {}),
                ...(w.acceptanceEstimate !== null && w.acceptanceEstimate !== undefined ? { acceptanceEstimate: w.acceptanceEstimate } : {}),
                ...(typeof w.pollAfterSeconds === 'number' ? { pollAfterSeconds: w.pollAfterSeconds } : {}),
                ...(w.cancelAfterAssignment !== null && w.cancelAfterAssignment !== undefined ? { cancelAfterAssignment: w.cancelAfterAssignment } : {}),
                ...(w.pickupWait !== null && w.pickupWait !== undefined ? { pickupWait: decodePickupWait(w.pickupWait) } : {}),
                ...(w.group !== null && w.group !== undefined ? { group: w.group } : {}),
                ...(w.tip !== null && w.tip !== undefined ? { tip: decodeTip(w.tip) } : {}),
                ...(w.safetyCheckIn !== null && w.safetyCheckIn !== undefined ? { safetyCheckIn: w.safetyCheckIn } : {}),
                ...(w.autoShare !== null && w.autoShare !== undefined ? { autoShare: w.autoShare } : {}),
                ...(w.pickupChangeRule !== null && w.pickupChangeRule !== undefined ? { pickupChangeRule: w.pickupChangeRule } : {}),
                ...(() => {
                    const cashShortfall = w.cashShortfall !== null && w.cashShortfall !== undefined ? decodeCashShortfall(w.cashShortfall) : undefined;
                    return cashShortfall !== undefined ? { cashShortfall } : {};
                })(),
                ...(() => {
                    const cashDue = w.cashDue !== null && w.cashDue !== undefined ? decodeCashDue(w.cashDue) : undefined;
                    return cashDue !== undefined ? { cashDue } : {};
                })(),
            },
        };
    }
    /**
     * D689 (gap 16): request several categories at once - one quote per category. Only where the market enables it (`MULTICATEGORY_NOT_ENABLED`), at most its
     * `maxCategories` (`MULTICATEGORY_TOO_MANY_CATEGORIES`), one quote per category (`MULTICATEGORY_CATEGORIES_NOT_DISTINCT`); idempotent by `idempotencyKey`. The answer is
     * the request's state (`searching`, with a lane per category and `primaryRideId`, the ride to follow first).
     */
    requestGroup(input) {
        const { changeFor, ...payment } = input.payment;
        const body = { ...input, payment: { ...payment, ...(changeFor !== undefined ? { changeFor: serializeMoney(changeFor) } : {}) } };
        return this.client.request({ method: 'POST', path: '/v1/ride-groups', body, auth: true });
    }
    /** Where the multicategory request a ride belongs to stands (404 `RIDE_GROUP_NOT_FOUND` for a ride of no group). */
    group(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/rider/rides/${encodeURIComponent(rideId)}/group`, auth: true });
    }
    /** Stop the whole multicategory search (every lane is cancelled, free). `409 RIDE_GROUP_ALREADY_MATCHED` once a driver took one of them (`params.winnerRideId`). */
    cancelGroup(rideId) {
        return this.client.request({ method: 'POST', path: `/v1/rider/rides/${encodeURIComponent(rideId)}/group/cancel`, body: {}, auth: true });
    }
    /**
     * D710 (doc 28, gap G9): the server's one "add a category" suggestion for a ride being searched (`offer`), or none with the reason. It quotes the suggested
     * category for this trip (the rider's quote limit applies) and is reused for a minute; nothing is requested or charged by asking.
     */
    async addCategoryOffer(rideId) {
        const res = await this.client.request({ method: 'POST', path: `/v1/rider/rides/${encodeURIComponent(rideId)}/add-category/offer`, body: {}, auth: true });
        if (!res.ok)
            return res;
        const { offer, ...rest } = res.value;
        return { ok: true, value: { ...rest, ...(offer !== null && offer !== undefined ? { offer: { ...offer, price: parseMoney(offer.price) } } : {}) } };
    }
    /**
     * D710: add the suggested category to the running search (the rider's tap) - the request becomes (or grows as) a D689 multicategory request: the first driver
     * who accepts in any of its categories takes it. Idempotent by `idempotencyKey`. Refusals: `ADD_CATEGORY_SEARCH_OVER`, `RIDE_GROUP_ALREADY_MATCHED`,
     * `ADD_CATEGORY_CATEGORY_TAKEN`, `MULTICATEGORY_TOO_MANY_CATEGORIES`, `MULTICATEGORY_NOT_ENABLED`, `QUOTE_EXPIRED`, …
     */
    addCategory(rideId, input) {
        return this.client.request({ method: 'POST', path: `/v1/rider/rides/${encodeURIComponent(rideId)}/add-category`, body: input, auth: true });
    }
    /** Rate a completed ride once (1–5 stars + optional tag tokens). An identical replay returns `replayed: true`. */
    rate(rideId, input) {
        return this.client.request({ method: 'POST', path: `/v1/rider/rides/${encodeURIComponent(rideId)}/rating`, body: input, auth: true });
    }
    /** The rider's rating of a ride (404 `RATING_NOT_FOUND` when it is not rated yet). */
    rating(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/rider/rides/${encodeURIComponent(rideId)}/rating`, auth: true });
    }
    /**
     * D695 (gap 17): say a tip for the driver of a completed ride - one of the market's presets, in the ride's currency, inside the market's window. CASH in the pilot: the
     * rider hands it to the driver, who confirms it; nothing is charged. Idempotent by `idempotencyKey` (an identical request is `replayed`); one tip per ride
     * (`TIP_ALREADY_GIVEN`, `TIP_ID_CONFLICT`); the stable refusals are `TIP_NOT_ENABLED`, `TIP_AMOUNT_NOT_OFFERED`, `TIP_CURRENCY_MISMATCH`, `TIP_RIDE_NOT_COMPLETED`, `TIP_WINDOW_CLOSED`.
     */
    async giveTip(rideId, input) {
        const res = await this.client.request({
            method: 'POST',
            path: `/v1/rider/rides/${encodeURIComponent(rideId)}/tip`,
            body: { idempotencyKey: input.idempotencyKey, amount: serializeMoney(input.amount) },
            auth: true,
        });
        if (!res.ok)
            return res;
        return { ok: true, value: { tip: decodeTip(res.value.tip), replayed: res.value.replayed } };
    }
    /** D695: the tip the rider gave on a ride (`tip: null` while none). */
    async tip(rideId) {
        const res = await this.client.request({ method: 'GET', path: `/v1/rider/rides/${encodeURIComponent(rideId)}/tip`, auth: true });
        if (!res.ok)
            return res;
        return { ok: true, value: { tip: res.value.tip === null || res.value.tip === undefined ? null : decodeTip(res.value.tip) } };
    }
    /**
     * The receipt of a completed ride as a one-page PDF (`GET /v1/rider/rides/:rideId/receipt.pdf`): the bytes with the file name the server suggested. The ride must be the
     * caller's own and completed (403 `RIDE_NOT_OWNED`, 409 `RIDE_NOT_COMPLETED`).
     */
    async receiptPdf(rideId) {
        const res = await this.client.request({ method: 'GET', path: `/v1/rider/rides/${encodeURIComponent(rideId)}/receipt.pdf`, auth: true, expect: 'bytes', timeoutMs: 60_000 });
        return res.ok ? { ok: true, value: receiptPdfFileOf(res.value) } : res;
    }
    /** The receipt of a completed ride (fare breakdown, payment + settlement status, market currency + timezone). */
    async receipt(rideId) {
        const res = await this.client.request({ method: 'GET', path: `/v1/rider/rides/${encodeURIComponent(rideId)}/receipt`, auth: true });
        if (!res.ok)
            return res;
        const { charged, tip, payment, contestCredit, surgeRetention: retentionWire, destinationChange: changeWire, ...w } = res.value.receipt;
        const { cashShortfall: shortfallWire, priorBalanceCollected, creditApplied, priorBalanceItems, ...paymentRest } = payment;
        const cashShortfall = shortfallWire !== undefined && shortfallWire !== null ? decodeCashShortfall(shortfallWire) : undefined;
        // D832: a malformed amount is dropped (never shown wrong).
        const positive = (m) => {
            if (m === undefined || m === null)
                return undefined;
            try {
                const v = parseMoney(m);
                return v.amountMinor > 0n ? v : undefined;
            }
            catch {
                return undefined;
            }
        };
        const prior = positive(priorBalanceCollected);
        const credit = positive(creditApplied);
        // D854: a malformed amount drops its line (never shown wrong).
        const safeMoney = (m) => {
            if (m === undefined || m === null)
                return undefined;
            try {
                return parseMoney(m);
            }
            catch {
                return undefined;
            }
        };
        const items = (priorBalanceItems ?? []).flatMap((i) => {
            const amount = positive(i.amount);
            return amount === undefined ? [] : [{ kind: i.kind, reasonCode: i.reasonCode, amount }];
        });
        const changeBefore = safeMoney(changeWire?.previousTotal);
        const changeAfter = safeMoney(changeWire?.total);
        const coupon = w.fare.coupon !== undefined ? safeMoney(w.fare.coupon.appliedDiscount) : undefined;
        const incentive = w.fare.incentive !== undefined ? safeMoney(w.fare.incentive.appliedDiscount) : undefined;
        const minimum = w.fare.minimumFare !== undefined ? safeMoney(w.fare.minimumFare.amount) : undefined;
        const contest = positive(contestCredit?.amount);
        const retained = positive(retentionWire?.amount);
        const increment = positive(retentionWire?.surgeIncrement);
        return {
            ok: true,
            value: {
                ...w,
                payment: { ...paymentRest, ...(cashShortfall !== undefined ? { cashShortfall } : {}), ...(prior !== undefined ? { priorBalanceCollected: prior } : {}), ...(prior !== undefined && items.length > 0 ? { priorBalanceItems: items } : {}), ...(credit !== undefined ? { creditApplied: credit } : {}) },
                ...(changeWire !== undefined ? { destinationChange: { at: changeWire.at, count: changeWire.count, ...(changeBefore !== undefined ? { previousTotal: changeBefore } : {}), ...(changeAfter !== undefined ? { total: changeAfter } : {}), ...(changeWire.basis !== undefined ? { basis: changeWire.basis } : {}), ...(changeWire.reasonCode !== undefined ? { reasonCode: changeWire.reasonCode } : {}), ...(changeWire.policyVersion !== undefined ? { policyVersion: changeWire.policyVersion } : {}) } } : {}),
                ...(contest !== undefined ? { contestCredit: { amount: contest, destination: 'RIDER_BALANCE' } } : {}),
                ...(retentionWire !== undefined && retained !== undefined && increment !== undefined ? { surgeRetention: { payer: 'PLATFORM', amount: retained, surgeIncrement: increment, retentionBp: retentionWire.retentionBp, reasonCode: retentionWire.reasonCode } } : {}),
                ...(tip !== undefined ? { tip: decodeTip(tip) } : {}),
                fare: {
                    pricingVersion: w.fare.pricingVersion,
                    source: w.fare.source,
                    lines: w.fare.lines.map((l) => ({ code: l.code, amount: parseMoney(l.amount) })),
                    ...(w.fare.grossTotal !== undefined ? { grossTotal: parseMoney(w.fare.grossTotal) } : {}),
                    total: parseMoney(w.fare.total),
                    ...(w.fare.platformDiscount !== undefined ? { platformDiscount: { payer: w.fare.platformDiscount.payer, amount: parseMoney(w.fare.platformDiscount.amount) } } : {}),
                    ...(w.fare.surge !== undefined ? { surge: w.fare.surge } : {}),
                    ...(w.fare.coupon !== undefined && coupon !== undefined ? { coupon: { code: w.fare.coupon.code, reasonCode: w.fare.coupon.reasonCode, policyVersion: w.fare.coupon.policyVersion, appliedDiscount: coupon } } : {}),
                    ...(w.fare.incentive !== undefined && incentive !== undefined ? { incentive: { reasonCode: w.fare.incentive.reasonCode, policyVersion: w.fare.incentive.policyVersion, appliedDiscount: incentive } } : {}),
                    ...(w.fare.minimumFare !== undefined && minimum !== undefined ? { minimumFare: { amount: minimum, pricingVersion: w.fare.minimumFare.pricingVersion, reasonCode: w.fare.minimumFare.reasonCode, ...(w.fare.minimumFare.policyVersion !== undefined ? { policyVersion: w.fare.minimumFare.policyVersion } : {}) } } : {}),
                },
                ...(charged !== undefined ? { charged: parseMoney(charged) } : {}),
            },
        };
    }
}
function decodePickupWait(w) {
    const { fee, ...rest } = w;
    return { ...rest, ...(fee !== undefined ? { fee: { ...fee, rate: parseMoney(fee.rate), penalty: parseMoney(fee.penalty) } } : {}) };
}
function decodeRiderRide(w) {
    const { requestDetails, destinationChange, pickupChange, noShowFee, ...rest } = w;
    return {
        ...rest,
        ...(noShowFee !== undefined && noShowFee !== null
            ? { noShowFee: { amount: parseMoney(noShowFee.amount), reasonCode: noShowFee.reasonCode, ...(noShowFee.policyVersion !== undefined ? { policyVersion: noShowFee.policyVersion } : {}) } }
            : {}),
        fare: { total: parseMoney(w.fare.total), pricingVersion: w.fare.pricingVersion },
        ...(pickupChange !== undefined && pickupChange !== null
            ? {
                pickupChange: {
                    at: pickupChange.at, acknowledged: pickupChange.acknowledged, count: pickupChange.count,
                    ...(pickupChange.fareReasonCode !== undefined ? { fareReasonCode: pickupChange.fareReasonCode } : {}),
                    ...(pickupChange.previousTotal !== undefined ? { previousTotal: parseMoney(pickupChange.previousTotal) } : {}),
                    ...(pickupChange.total !== undefined ? { total: parseMoney(pickupChange.total) } : {}),
                },
            }
            : {}),
        ...(destinationChange !== undefined && destinationChange !== null
            ? { destinationChange: { at: destinationChange.at, acknowledged: destinationChange.acknowledged, count: destinationChange.count, ...(destinationChange.previousTotal !== undefined ? { previousTotal: parseMoney(destinationChange.previousTotal) } : {}), ...(destinationChange.total !== undefined ? { total: parseMoney(destinationChange.total) } : {}) } }
            : {}),
        ...(requestDetails !== undefined && requestDetails !== null
            ? {
                requestDetails: {
                    ...(requestDetails.cashChangeFor !== undefined ? { cashChangeFor: parseMoney(requestDetails.cashChangeFor) } : {}),
                    ...(requestDetails.pickupNote !== undefined ? { pickupNote: requestDetails.pickupNote } : {}),
                    ...(requestDetails.ridePreferences !== undefined ? { ridePreferences: requestDetails.ridePreferences } : {}),
                },
            }
            : {}),
    };
}
function decodeCashShortfall(w) {
    try {
        const due = parseMoney(w.due);
        const received = parseMoney(w.received);
        const shortfall = parseMoney(w.shortfall);
        if (received.currency !== due.currency || shortfall.currency !== due.currency || shortfall.amountMinor <= 0n)
            return undefined;
        return { outcome: w.outcome, due, received, shortfall };
    }
    catch {
        return undefined;
    }
}
function decodeCashDue(w) {
    try {
        const amount = parseMoney(w.amount);
        const fare = parseMoney(w.fare);
        const prior = w.priorBalance !== undefined ? parseMoney(w.priorBalance) : undefined;
        const credit = w.creditApplied !== undefined ? parseMoney(w.creditApplied) : undefined;
        // Only a self-consistent figure is trusted: one currency, and the amount is the fare less the credit line plus the prior line.
        if (fare.currency !== amount.currency || (prior !== undefined && prior.currency !== amount.currency) || (credit !== undefined && credit.currency !== amount.currency))
            return undefined;
        if (credit !== undefined && (credit.amountMinor < 0n || credit.amountMinor > fare.amountMinor))
            return undefined;
        if (amount.amountMinor !== fare.amountMinor - (credit?.amountMinor ?? 0n) + (prior?.amountMinor ?? 0n))
            return undefined;
        return { amount, fare, ...(prior !== undefined && prior.amountMinor > 0n ? { priorBalance: prior } : {}), ...(credit !== undefined && credit.amountMinor > 0n ? { creditApplied: credit } : {}) };
    }
    catch {
        return undefined;
    }
}
function decodeTip(w) {
    return { ...w, amount: parseMoney(w.amount) };
}
/**
 * Decode a market's `verticals`: `{ enabled: string[], policyVersion?: string }`, or undefined when absent or not that shape (a malformed block is read as
 * "unknown" - never as "everything offered").
 */
export function decodeRiderMarketVerticals(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return undefined;
    const o = v;
    if (!Array.isArray(o.enabled) || !o.enabled.every((x) => typeof x === 'string' && /^[a-z][a-z0-9_]{0,31}$/.test(x)))
        return undefined;
    if (o.policyVersion !== undefined && typeof o.policyVersion !== 'string')
        return undefined;
    return { enabled: [...o.enabled], ...(typeof o.policyVersion === 'string' ? { policyVersion: o.policyVersion } : {}) };
}
/** A market as the client reads it: the `verticals` block decoded (dropped when malformed), everything else as served. */
export function decodeRiderMarket(m) {
    const raw = m.verticals;
    if (raw === undefined)
        return m;
    const verticals = decodeRiderMarketVerticals(raw);
    const { verticals: _dropped, ...rest } = m;
    return verticals !== undefined ? { ...rest, verticals } : rest;
}
const finite = (v) => typeof v === 'number' && Number.isFinite(v);
/** A defensive read of the wire: a car without a finite point is dropped, an unknown member ignored. */
export function decodeRiderNearbyVehicles(raw) {
    if (typeof raw !== 'object' || raw === null)
        return undefined;
    const r = raw;
    if (typeof r.enabled !== 'boolean' || !Array.isArray(r.vehicles) || !finite(r.refreshSeconds))
        return undefined;
    const vehicles = [];
    for (const v of r.vehicles) {
        if (typeof v !== 'object' || v === null)
            continue;
        const c = v;
        if (!finite(c.latitude) || !finite(c.longitude) || Math.abs(c.latitude) > 90 || Math.abs(c.longitude) > 180)
            continue;
        vehicles.push({
            latitude: c.latitude,
            longitude: c.longitude,
            ...(typeof c.vehicleClass === 'string' && c.vehicleClass !== '' ? { vehicleClass: c.vehicleClass } : {}),
            ...(finite(c.headingDegrees) ? { headingDegrees: c.headingDegrees } : {}),
        });
    }
    return {
        marketId: typeof r.marketId === 'string' ? r.marketId : '',
        enabled: r.enabled,
        vehicles,
        refreshSeconds: r.refreshSeconds,
        reasonCode: typeof r.reasonCode === 'string' ? r.reasonCode : '',
        policyVersion: typeof r.policyVersion === 'string' ? r.policyVersion : '',
        at: typeof r.at === 'string' ? r.at : '',
    };
}
export class RiderMarketsApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /** D821: the cars around a point of a served market (RIDER session; approximate by construction). A body the app cannot read is `MALFORMED_RESPONSE`, never an invented map. */
    async nearbyVehicles(marketId, point) {
        const qs = `?marketId=${encodeURIComponent(marketId)}&latitude=${encodeURIComponent(String(point.latitude))}&longitude=${encodeURIComponent(String(point.longitude))}`;
        const res = await this.client.request({ method: 'GET', path: `/v1/rider/nearby-vehicles${qs}`, auth: true });
        if (!res.ok)
            return res;
        const decoded = decodeRiderNearbyVehicles(res.value);
        return decoded !== undefined ? { ok: true, value: decoded } : { ok: false, error: transportError('MALFORMED_RESPONSE', 'nearby vehicles: unreadable body') };
    }
    /** The markets a rider can ride in (served launch statuses only). */
    async list() {
        const res = await this.client.request({ method: 'GET', path: '/v1/rider/markets', auth: true });
        return res.ok && Array.isArray(res.value.markets) ? { ok: true, value: { ...res.value, markets: res.value.markets.map(decodeRiderMarket) } } : res;
    }
    /** The served market nearest to a point (404 `MARKET_NOT_SERVED` when none is close enough). */
    resolve(point) {
        const qs = `?latitude=${encodeURIComponent(String(point.latitude))}&longitude=${encodeURIComponent(String(point.longitude))}`;
        return this.client.request({ method: 'GET', path: `/v1/rider/markets/resolve${qs}`, auth: true }).then((res) => res.ok && res.value.market !== undefined ? { ok: true, value: { ...res.value, market: decodeRiderMarket(res.value.market) } } : res);
    }
    /** The categories and payment methods a served market offers now. */
    rideOptions(marketId) {
        return this.client.request({ method: 'GET', path: `/v1/rider/markets/${encodeURIComponent(marketId)}/ride-options`, auth: true });
    }
}
//# sourceMappingURL=rides.js.map